/**
 * Default configuration for signature compression.
 */
export const SIGNATURE_COMPRESSION_CONFIG = {
  MAX_WIDTH: 600,
  MAX_HEIGHT: 300,
  QUALITY: 0.5,
  SIZE_THRESHOLD: 5000,
};

/**
 * Compresses a base64 image string by resizing it to a maximum dimension
 * and adjusting its quality using HTML5 Canvas.
 * 
 * @param base64Str The source base64 string
 * @param maxWidth The maximum width for the output image
 * @param maxHeight The maximum height for the output image
 * @param quality The image quality (0 to 1)
 * @returns A Promise resolving to the compressed base64 string
 */
export async function compressBase64Image(
  base64Str: string,
  maxWidth: number = SIGNATURE_COMPRESSION_CONFIG.MAX_WIDTH,
  maxHeight: number = SIGNATURE_COMPRESSION_CONFIG.MAX_HEIGHT,
  quality: number = SIGNATURE_COMPRESSION_CONFIG.QUALITY
): Promise<string> {
  return new Promise((resolve, reject) => {
    // Basic validation for base64 string
    if (!base64Str || typeof base64Str !== 'string' || !base64Str.startsWith('data:image')) {
      reject(new Error('Invalid base64 image string provided for compression'));
      return;
    }

    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Resizing logic
      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context for image compression'));
        return;
      }

      // Fill background with white (JPEG doesn't support transparency)
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);

      // Draw the image
      ctx.drawImage(img, 0, 0, width, height);

      // Export as JPEG with quality compression
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedDataUrl);
    };
    img.onerror = (error) => reject(new Error('Failed to load image for compression: ' + error));
  });
}

/**
 * Compresses a File object by converting to base64, compressing, and converting back to Blob.
 */
export async function compressImageFile(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      try {
        let base64 = reader.result as string;
        if (base64 && base64.length > SIGNATURE_COMPRESSION_CONFIG.SIZE_THRESHOLD) {
          base64 = await compressBase64Image(base64);
        }
        
        // Convert base64 back to Blob
        const byteString = atob(base64.split(',')[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: 'image/jpeg' });
        resolve(blob);
      } catch (e) {
        // Fallback to original file if compression fails
        resolve(file);
      }
    };
    reader.onerror = () => resolve(file);
  });
}
